import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Box,
} from "@mui/material";
import IosShareRounded from "@mui/icons-material/IosShareRounded";
import AddBoxOutlined from "@mui/icons-material/AddBoxOutlined";
import CheckRounded from "@mui/icons-material/CheckRounded";
import InstallMobileRounded from "@mui/icons-material/InstallMobileRounded";
import { useInstallApp } from "hooks/useInstallApp";

/**
 * Walks the user through putting HealthDesk on their home screen.
 *
 * On Chromium this is a single button, because the browser gives us an install
 * event to fire. Everywhere else the browser will only install from a menu the
 * user opens themselves, so all we can do is say which menu and what to tap.
 *
 * On iOS, that "everywhere else" is Safari only — Apple never gave other iOS
 * browsers (Chrome, Brave, Firefox, Edge, ...) the ability to add a real
 * standalone app to the home screen, even though they all run on WebKit. So
 * "ios-chrome" and "ios-other" get told to switch to Safari, not fake steps.
 */

const NON_SAFARI_IOS_METHODS = new Set(["ios-chrome", "ios-other"]);

const STEPS = {
  "ios-safari": [
    {
      icon: <IosShareRounded />,
      text: "Tap the Share button at the bottom of Safari.",
    },
    {
      icon: <AddBoxOutlined />,
      text: 'Scroll down and tap "Add to Home Screen".',
    },
    { icon: <CheckRounded />, text: 'Tap "Add" in the top right corner.' },
  ],
  "macos-safari": [
    {
      icon: <IosShareRounded />,
      text: "Open the Share menu in the Safari toolbar.",
    },
    { icon: <AddBoxOutlined />, text: 'Choose "Add to Dock".' },
    { icon: <CheckRounded />, text: 'Click "Add" to confirm.' },
  ],
};

const InstallAppDialog = ({ open, onClose }) => {
  const { method, canPromptDirectly, promptInstall } = useInstallApp();
  const needsSafari = NON_SAFARI_IOS_METHODS.has(method);
  const steps = STEPS[method];

  // iOS gives an installed app its own storage, separate from the browser's, so
  // the signed-in session does not come across and the first launch starts at
  // the login screen. Saying so up front stops it reading as a bug. Android and
  // desktop share storage with the browser and stay signed in.
  const signsOutOnFirstOpen = method.startsWith("ios");

  const handleInstall = async () => {
    await promptInstall();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add HealthDesk to your home screen</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Open HealthDesk from your home screen, like any other app.
        </DialogContentText>

        {signsOutOnFirstOpen && (
          <DialogContentText sx={{ pt: 1 }} color="text.secondary">
            Please sign in once when you first open it.
          </DialogContentText>
        )}

        {canPromptDirectly && (
          <Box sx={{ display: "flex", justifyContent: "center", pt: 3, pb: 1 }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<InstallMobileRounded />}
              onClick={handleInstall}
            >
              Install
            </Button>
          </Box>
        )}

        {!canPromptDirectly && needsSafari && (
          <DialogContentText sx={{ pt: 2 }}>
            iOS only allows adding apps to the home screen from Safari.
            Please open this page in Safari, then tap Share and choose
            "Add to Home Screen".
          </DialogContentText>
        )}

        {!canPromptDirectly && !needsSafari && steps && (
          <List>
            {steps.map((step) => (
              <ListItem key={step.text} disableGutters>
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: "primary.main" }}>{step.icon}</Avatar>
                </ListItemAvatar>
                <ListItemText primary={step.text} />
              </ListItem>
            ))}
          </List>
        )}

        {!canPromptDirectly && !needsSafari && !steps && (
          <DialogContentText sx={{ pt: 2 }} color="text.secondary">
            Please look for "Install" or "Add to Home Screen" in your browser's
            menu.
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default InstallAppDialog;
